import { Menu, MenuItem } from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendSelectionCommand } from '../../../core/mapV2/commands/ExtendSelectionCommand';
import { extendHints } from '../../../core/mapV2/selection';
import { roadSectionsQuery } from '../../../core/reference/queries';
import { useAppDispatch, useAppSelector } from '../../../types';
import type { SectionContextMenuTarget } from './useReferenceInteractions';

// Right-click on a section: shortcuts to extend the selection from it.
export const SectionContextMenu: FC<{ target: SectionContextMenuTarget | null; onClose: () => void }> = ({
    target,
    onClose,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const sections = useAppSelector(roadSectionsQuery);
    const hints = useMemo(() => {
        const base = target ? sections.find((section) => section.id === target.id) : undefined;

        return base ? extendHints(sections, base) : [];
    }, [sections, target]);
    const axis = hints.find((hint) => hint.kind === 'axis')?.value;

    return (
        <Menu
            open={target !== null && hints.length > 0}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={target ? { top: target.y, left: target.x } : undefined}
            // The menu opens under the pointer: a second right-click on it must not open the browser's menu.
            onContextMenu={(event) => event.preventDefault()}
        >
            {hints.map((hint) => (
                <MenuItem
                    key={hint.kind}
                    sx={{ fontSize: 13, minWidth: 250, display: 'flex', justifyContent: 'space-between', gap: 2 }}
                    onClick={() => {
                        dispatch(ExtendSelectionCommand({ kind: hint.kind, baseId: target!.id }));
                        onClose();
                    }}
                >
                    <span>{t(`mapV2.extend.${hint.kind}`, { value: axis })}</span>
                    <span style={{ color: '#94A3B8', fontSize: 11 }}>
                        {hint.kind === 'axis' ? t('mapV2.extend.axisHint', { count: hint.count }) : hint.value}
                    </span>
                </MenuItem>
            ))}
        </Menu>
    );
};
